import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Eye, EyeOff, Loader2, CheckCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AffiliateLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/affiliates/login`, { email, password });
      localStorage.setItem('affiliate_token', res.data.token);
      localStorage.setItem('affiliate_data', JSON.stringify(res.data.affiliate));
      toast.success(`Bem-vindo, ${res.data.affiliate.name}!`);
      navigate('/afiliados/painel');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) { toast.error('Digite seu e-mail'); return; }
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail, type: 'affiliate' });
      setForgotSent(true);
      toast.success('E-mail de recuperação enviado!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar e-mail');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0A2647] to-[#071A33] flex items-center justify-center px-4">
      <Card className="max-w-md w-full border-0 shadow-2xl">
        <CardContent className="p-8">
          <div className="text-center mb-6">
            <Link to="/">
              <span className="text-[#C5A059] font-bold text-lg" style={{ fontFamily: 'Montserrat, sans-serif' }}>Falo Com O Dono</span>
            </Link>
            <h2 className="text-xl font-bold text-slate-900 mt-3">Painel do Afiliado</h2>
            <p className="text-sm text-slate-500">Entre com seu e-mail e senha</p>
          </div>

          {forgotMode ? (
            forgotSent ? (
              <div className="text-center py-4">
                <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-7 h-7 text-emerald-600" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">E-mail Enviado!</h3>
                <p className="text-sm text-slate-500 mb-4">Verifique sua caixa de entrada e siga as instrucoes para redefinir sua senha.</p>
                <button onClick={() => { setForgotMode(false); setForgotSent(false); }} className="text-[#C5A059] font-semibold text-sm hover:underline" data-testid="aff-back-to-login-btn">
                  Voltar ao Login
                </button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="text-center mb-2">
                  <h3 className="text-lg font-bold text-slate-900">Recuperar Senha</h3>
                  <p className="text-sm text-slate-500">Digite seu e-mail para receber o link de recuperacao</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">E-mail</label>
                  <Input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required className="h-11" placeholder="seu@email.com" data-testid="aff-forgot-email-input" />
                </div>
                <Button type="submit" className="w-full bg-[#C5A059] hover:bg-[#C5A059]/90 text-[#0A2647] font-bold h-11" disabled={forgotLoading} data-testid="aff-forgot-submit-btn">
                  {forgotLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Enviando...</> : 'Enviar Link de Recuperacao'}
                </Button>
                <button type="button" onClick={() => setForgotMode(false)} className="w-full text-sm text-slate-500 hover:text-slate-700 mt-2" data-testid="aff-cancel-forgot-btn">
                  Voltar ao Login
                </button>
              </form>
            )
          ) : (
            <>
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">E-mail</label>
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-11" placeholder="seu@email.com" data-testid="aff-login-email" />
                </div>
                <div className="relative">
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm font-medium text-slate-700">Senha</label>
                    <button type="button" onClick={() => setForgotMode(true)} className="text-xs text-[#C5A059] hover:underline font-medium" data-testid="aff-forgot-password-link">
                      Esqueci minha senha
                    </button>
                  </div>
                  <Input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required className="h-11 pr-10" placeholder="Sua senha" data-testid="aff-login-password" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-[34px] text-slate-400">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-[#0A2647] hover:bg-[#0A2647]/90 text-white font-semibold h-11" data-testid="aff-login-submit">
                  {loading ? 'Entrando...' : 'Entrar'}
                </Button>
              </form>

              <p className="text-sm text-slate-500 text-center mt-4">
                Não tem conta? <Link to="/afiliados" className="text-[#C5A059] font-semibold hover:underline">Cadastre-se como Afiliado</Link>
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AffiliateLogin;
